import { useMemo } from "react";
import { MenuItem, Select, Typography, Chip, Box, alpha } from "@mui/material";
import { useGetOne } from "react-admin";
import { usePolicyContext } from "./PolicyContext";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { PolicyStatus, ResourceName, type Policy, type Vendor } from "@/types";

/** Returns a Set of "carrier|start|end" keys that appear more than once in the list */
const getDuplicatePolicyKeys = (policies: Policy[]): Set<string> => {
  const counts = new Map<string, number>();
  for (const p of policies) {
    const key = `${p.source_data?.policy_carrier}|${p.effective_start_date}|${p.effective_end_date}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const dupes = new Set<string>();
  for (const [key, count] of counts) {
    if (count > 1) dupes.add(key);
  }
  return dupes;
};

const policyKey = (p: Policy) => `${p.source_data?.policy_carrier}|${p.effective_start_date}|${p.effective_end_date}`;

interface PolicySelectorProps {
  label?: string;
  size?: "small" | "medium";
  minWidth?: number;
  showAllOption?: boolean;
}

/** Resolves carrier name for a policy via vendor ref */
const useCarrierName = (policy?: Policy): { name: string; isLoading: boolean } => {
  const vendorIdRef = policy?.vendor_id_ref;
  const { data: carrier, isLoading } = useGetOne<Vendor>(
    ResourceName.VENDORS,
    { id: vendorIdRef ?? "" },
    { enabled: !!vendorIdRef }
  );

  if (!vendorIdRef) return { name: "No Carrier", isLoading: false };
  if (carrier?.company_name) return { name: carrier.company_name, isLoading: false };
  if (isLoading) return { name: "—", isLoading: true };
  return { name: "No Carrier", isLoading: false };
};

/** Inline display for single-policy claimants */
const SinglePolicyDisplay = ({ policy, showPolicyId = false }: { policy: Policy; showPolicyId?: boolean }) => {
  const { name: carrierName } = useCarrierName(policy);
  const isActive = policy.status === PolicyStatus.ACTIVE;
  const yearRange = formatYearRange(policy.effective_start_date, policy.effective_end_date);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: 1.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
        Viewing policy:
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {carrierName}
      </Typography>
      <Chip label={yearRange} size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600 }} />
      {showPolicyId && policy.source_data?.source_policy_id && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          {policy.source_data.source_policy_id}
        </Typography>
      )}
      {isActive && (
        <Chip label="Active" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
      )}
    </Box>
  );
};

/**
 * Dropdown to select the active policy from the PolicyContext.
 * Shows carrier name + year range, consistent with Policy Show header and claimant list.
 */
export const PolicySelector = ({ showAllOption = false }: PolicySelectorProps) => {
  const { policies, selectedPolicyId, setSelectedPolicyId } = usePolicyContext();
  const duplicateKeys = useMemo(() => getDuplicatePolicyKeys(policies), [policies]);

  // Single policy, no "All" option — just display it inline
  if (policies.length <= 1 && !showAllOption) {
    const policy = policies[0];
    if (!policy) return null;
    return <SinglePolicyDisplay policy={policy} />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: 1.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
        Viewing by:
      </Typography>
      <Select
        value={selectedPolicyId ?? ""}
        onChange={(e) => setSelectedPolicyId(e.target.value || undefined)}
        variant="standard"
        disableUnderline
        displayEmpty
        renderValue={(value) => {
          if (!value) return "All Policies";
          const p = policies.find((pol) => pol.id === value);
          if (!p) return "All Policies";
          const carrierName = ((p as unknown as Record<string, unknown>).vendor_company_name as string) || "No Carrier";
          const years = formatYearRange(p.effective_start_date, p.effective_end_date);
          const base = `${carrierName}${years ? ` · ${years}` : ""}`;
          const needsDisambiguation = duplicateKeys.has(policyKey(p)) && p.source_data?.source_policy_id;
          return needsDisambiguation ? `${base} (${p.source_data!.source_policy_id})` : base;
        }}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          PaperProps: { sx: { maxHeight: 300, maxWidth: 400 } },
        }}
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          "& .MuiSelect-select": { py: 0, pr: 3 },
        }}
      >
        {showAllOption && <MenuItem value="">All Policies</MenuItem>}
        {policies.map((policy) => (
          <MenuItem key={policy.id} value={policy.id}>
            <PolicyMenuItem policy={policy} showPolicyId={duplicateKeys.has(policyKey(policy))} />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

/** Single menu item showing carrier + year + status */
const PolicyMenuItem = ({ policy, showPolicyId = false }: { policy: Policy; showPolicyId?: boolean }) => {
  const { name: carrierName } = useCarrierName(policy);
  const isActive = policy.status === PolicyStatus.ACTIVE;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {carrierName}
      </Typography>
      <Chip
        label={formatYearRange(policy.effective_start_date, policy.effective_end_date)}
        size="small"
        sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600 }}
      />
      {showPolicyId && policy.source_data?.source_policy_id && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          {policy.source_data.source_policy_id}
        </Typography>
      )}
      {isActive && (
        <Chip label="Active" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
      )}
    </Box>
  );
};
